import { useAppStore } from '@/store/store';
import { selectFilteredHistory } from '@/store/selectors';
import { useState, useEffect } from 'react';
import { CopyPlus, X, History as HistoryIcon, SearchX } from 'lucide-react';
import { EmptyState } from '@/components/shared/EmptyState';

function SkeletonRow() {
  return (
    <div
      className="card"
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 'var(--space-3)',
        opacity: 0.5,
      }}
      aria-hidden="true"
    >
      <div style={{ flex: 1 }}>
        <div style={{ width: '40%', height: 16, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-1)' }} />
        <div style={{ width: '25%', height: 12, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)', marginBottom: 'var(--space-1)' }} />
        <div style={{ width: '60%', height: 14, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)' }} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
        <div style={{ width: 32, height: 32, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)' }} />
        <div style={{ width: 32, height: 32, background: 'var(--color-border)', borderRadius: 'var(--radius-sm)' }} />
      </div>
    </div>
  );
}

export function HistoryList() {
  const calculations = useAppStore(selectFilteredHistory);
  const searchQuery = useAppStore((s) => s.searchQuery);
  const setSearchQuery = useAppStore((s) => s.setSearchQuery);
  const deleteCalculation = useAppStore((s) => s.deleteCalculation);
  const clearHistory = useAppStore((s) => s.clearHistory);
  const [loading, setLoading] = useState(true);

  // R17: Show skeleton briefly on first mount to indicate loading state
  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleClone = (calcId: string) => {
    const calc = calculations.find((c) => c.id === calcId);
    if (!calc) return;
    useAppStore.setState({
      packages: calc.shipment.packages.map((pkg, i) => ({
        ...pkg,
        id: `clone-${Date.now()}-${i}`,
      })),
      mode: calc.shipment.mode,
      dimDivisor: calc.shipment.dimDivisor,
      divisorOverridden: calc.shipment.overrideDivisor,
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
          <div style={{ flex: 1, height: 36, background: 'var(--color-border)', borderRadius: 'var(--radius-md)', opacity: 0.4 }} />
        </div>
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    );
  }

  if (calculations.length === 0 && !searchQuery) {
    return (
      <div className="card">
        <EmptyState
          icon={HistoryIcon}
          title="No saved calculations yet"
          description="Save calculations from this tab to recall them later, compare shipments, and see aggregate analytics across your history."
          hint="Use the “Save Current” button above to store the active calculation."
        />
      </div>
    );
  }

  if (calculations.length === 0 && searchQuery) {
    return (
      <div className="card">
        <EmptyState
          icon={SearchX}
          title="No matching calculations"
          description={`No saved calculations match “${searchQuery}”. Try a different name or clear the search.`}
          actions={[{ label: 'Clear search', onClick: () => setSearchQuery('') }]}
        />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <input
          type="search"
          placeholder="Search calculations..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search history"
          style={{
            flex: 1,
            padding: 'var(--space-2) var(--space-3)',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--color-border)',
            background: 'var(--color-bg-primary)',
            color: 'var(--color-text-primary)',
            fontSize: 'var(--font-size-sm)',
          }}
        />
        {calculations.length > 0 && (
          <button type="button" className="btn btn-sm" onClick={clearHistory} aria-label="Clear all history" style={{ color: 'var(--color-error)' }}>
            Clear All
          </button>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        {calculations.map((calc) => (
          <div
            key={calc.id}
            className="card"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 'var(--space-3)' }}
          >
            <div>
              <div style={{ fontWeight: 'var(--font-weight-semibold)' }}>{calc.name}</div>
              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                {new Date(calc.timestamp).toLocaleString()}
              </div>
              <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', marginTop: 'var(--space-1)' }}>
                {calc.summary.totalCBM.toFixed(4)} CBM · {calc.summary.chargeableWeightKg.toFixed(2)} kg · {calc.summary.totalPackages} pkg{calc.summary.totalPackages !== 1 ? 's' : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-1)' }}>
              <button type="button" className="btn btn-sm" onClick={() => handleClone(calc.id)} aria-label="Clone calculation" title="Clone to calculator">
                <CopyPlus size={14} />
              </button>
              <button type="button" className="btn btn-sm" onClick={() => deleteCalculation(calc.id)} aria-label={`Delete ${calc.name}`}>
                <X size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
